import { apiClient } from './client';
import { unwrapList } from '../utils/api';

/**
 * A course business's invitations and reminders (backend /crm/reminders; contract in
 * .claude/handoff/reminders-2026-10-18/spec.md section 5). ADMIN and the CRM roles SALES,
 * CALL_CENTER and ACCOUNT_MANAGER send; everyone reaches only their own tenant's students (a
 * platform ADMIN every tenant's).
 *
 * Channels are IN_APP, PUSH, TELEGRAM, SMS and EMAIL. Audiences are NO_COURSE, UNPAID,
 * NOT_STARTED, INACTIVE (3, 7 or 14 days) and SELECTED (an explicit list of user ids).
 */

/** { inApp: true, push, telegram, sms, email } — false = the server has no credentials for it (it is skipped). */
export async function getReminderChannels() {
  const { data } = await apiClient.get('/crm/reminders/channels');
  return data;
}

/**
 * The business's published courses (audience filter, the course a message opens): [{ id, title }].
 * Sales and the call centre cannot read /courses/admin, so this route exists for them.
 */
export async function getReminderCourses() {
  const { data } = await apiClient.get('/crm/reminders/courses');
  return Array.isArray(data) ? data : [];
}

/**
 * Who a message would reach, before it is sent.
 * params: { audience, courseId?, inactiveDays? (3 | 7 | 14), userIds? (an array of ids) }
 * -> { count, sample: [{ id, name, phone, email, course?: { id, title }, reason }], reach: { push, telegram, sms, email } }
 */
export async function getReminderAudience({ audience, courseId, inactiveDays, userIds } = {}) {
  const params = { audience };
  if (courseId) params.courseId = courseId;
  if (inactiveDays) params.inactiveDays = inactiveDays;
  if (Array.isArray(userIds) && userIds.length > 0) params.userIds = userIds.join(',');
  const { data } = await apiClient.get('/crm/reminders/audience', { params });
  return data;
}

/**
 * Send a message now (inside 09:00-21:00 Tashkent) or schedule it (`scheduledAt`, an ISO instant).
 * body: { audience, courseId?, inactiveDays?, userIds?, title, message, linkCourseId?, discountId?,
 *         channels: ['IN_APP', ...], smsFallbackOnly?, scheduledAt? } -> the campaign
 */
export async function createReminderCampaign(body) {
  const { data } = await apiClient.post('/crm/reminders/campaigns', body);
  return data;
}

/** { items: [campaign], meta: { total, page, limit, totalPages } } — params: page, limit */
export async function getReminderCampaigns(params = {}) {
  const { data } = await apiClient.get('/crm/reminders/campaigns', { params });
  return data;
}

/** The campaign + { byChannel: { IN_APP: { sent, failed, skipped }, ... }, deliveries: [{ user, channel, status, sentAt, clickedAt }] } */
export async function getReminderCampaign(id) {
  const { data } = await apiClient.get(`/crm/reminders/campaigns/${encodeURIComponent(id)}`);
  return data;
}

/** A SCHEDULED campaign only; nothing is sent afterwards. */
export async function cancelReminderCampaign(id) {
  const { data } = await apiClient.post(`/crm/reminders/campaigns/${encodeURIComponent(id)}/cancel`);
  return data;
}

/**
 * The nine automatic rules: [{ key, audience, days, hours, enabled, message (the text in use: the
 * standard one until it is changed), defaultMessage, channels, smsFallbackOnly, courseId, discountId,
 * stats30d: { sent, clicked, converted } }]
 */
export async function getReminderRules() {
  const { data } = await apiClient.get('/crm/reminders/rules');
  return unwrapList(data);
}

/**
 * body: { enabled?, message? ('' = back to the standard text), channels?, smsFallbackOnly?,
 * courseId? / discountId? (null clears) } -> the rule
 */
export async function updateReminderRule(key, body) {
  const { data } = await apiClient.patch(`/crm/reminders/rules/${encodeURIComponent(key)}`, body);
  return data;
}

/** What one person was sent, newest first (50): [{ channel, status, sentAt, clickedAt, ... }] */
export async function getUserReminders(userId) {
  const { data } = await apiClient.get(`/crm/reminders/user/${encodeURIComponent(userId)}`);
  return unwrapList(data);
}
